/*
 * Copyright (c) 2017 - 2020 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosResponse} from 'axios';
import isNil from 'lodash.isnil';
import qs from 'qs';
import {ApplicationContext} from '@ion/core/src/context/ApplicationContext';
import {error, info} from '../logger/logger';
import {contains} from '../utils/StringUtil';

/**
 * Enumerates the supported deployment environments for all iOn class applications.
 *
 * ```typescript
 *   import {
 *     Environment,
 *     getEnvironment
 *   } from '@ion/core/src/environment/environment';
 *   ...
 *
 *   const env: Environment = getEnvironment();
 *   if (env === Environment.us) {
 *     // handle US production environment...
 *   }
 * ```
 *
 * @see {@link getEnvironment}
 * @see {@link Environments}
 */
export enum Environment {
  local = 'local',
  dev = 'dev',
  qa = 'qa',
  stg = 'stg',
  us = 'us',
  eu = 'eu',
};

/**
 * Maps each Environment enum value to its corresponding base URL string.
 * Used to configure remote environment endpoints for loading configuration
 * documents from multiple deployment environments.
 *
 * ```typescript
 *   import {Environments, Environment, loadRemoteConfig} from '@ion/core/src/environment/environment';
 *   ...
 *
 *   const envMap: Environments = {
 *     [Environment.dev] : 'https://dev.example.com',
 *     [Environment.qa]  : 'https://qa.example.com',
 *     [Environment.us]  : 'https://us.example.com'
 *   };
 *   const config = await loadRemoteConfig(envMap, Environment.qa);
 * ```
 *
 * @see {@link loadRemoteConfig}
 * @see {@link Environment}
 */
export type Environments = Record<Environment, string>;

/**
 * Defines the configuration object loaded from the `environment.json` document.
 * Contains string keys mapped to JSON-compatible values including primitives,
 * objects, and arrays.
 *
 * ```typescript
 *   import {
 *     Config,
 *     loadExternalConfig,
 *     Environment
 *   } from '@ion/core/src/environment/environment';
 *   ...
 *
 *   const envMap: Record<Environment, string> = {
 *     [Environment.dev] : 'https://dev.example.com',
 *     [Environment.qa]  : 'https://qa.example.com'
 *   };
 *   const config: Config = await loadExternalConfig(envMap, Environment.qa);
 * ```
 *
 * @see {@link loadExternalConfig}
 * @see {@link loadCORSConfig}
 */
export interface Config {
  [key: string]: string | number | boolean | null | undefined | object | any[];
}

/**
 * Defines the environment object which holds all configuration values
 * provided by the environment.json document. This object is to be
 * considered private and only assigned a value via the load and /
 * or __inject__ functions, respectively.
 */
let _config: Config;

/**
 * The get method provides a single source of access to environment.json
 * configuration values. Calls to get ensure the underlying environment
 * configuration which has been loaded remain immutable.
 *
 * For a list of all available keys, see environment.json.
 *
 * ```typescript
 *   // get the appKey entry from environment.json ...
 *   const appKey = get('appKey'); // app key value ...
 *
 *   // get the passwordMinLength and passwordMaxLength entries ...
 *   const policy = get('passwordMinLength', 'passwordMaxLength');
 *   console.log(policy.passwordMinLength) // 'passwordMinLength' value ...
 *   console.log(policy.passwordMaxLength) // 'passwordMaxLength' value ...
 *
 * ```
 *
 * @see ./src/static/environment.json
 *
 * @method  get
 * @param   {string|array} entry Defines the environment.json configuration
 *          entry, or an array of entries, which are to be returned.
 * @returns {string|number|boolean} The value of the given environment.json
 *          configuration entry or entries.
 */
export const get = (...entry) => {
  if (_config && entry) {
    if (entry.length > 1) {
      const entries = {};
      entry.forEach(key => {
        entries[key] = _config[key];
      });
      return entries;
    }
    return _config[entry[0]];
  }
};

/**
 * This `isDevBuildEnvironment` function provides a utility for determining if the
 * application is running in a local development build environment. This is similar
 * to the `isDevelopmentEnvironment` function in that it determines if the app is
 * running from a local domain (i.e. localhost, etc.), while also providing a more
 * strict check to make sure that the actual build itself is a dev build, as a local
 * production build can still be run.
 *
 * ```typescript
 *   import {isLocalDevelopmentBuild} from '@ion/core/src/environment/environment';
 *   ...
 *
 *   // only execute when the dev build is running locally ...
 *   if (isLocalDevelopmentBuild()) {
 *     return (
 *        ...
 *     )
 *   }
 * ```
 *
 * @param   host The hostname to check. If not provided, defaults to the current
 *          hostname.
 * @param   includeLoopback Whether to include loopback addresses in the check.
 *          Defaults to `true`.
 * @returns `true` if the application is a local dev build, otherwise, `false`.
 */
export const isLocalDevelopmentBuild = (host?: string, includeLoopback = true): boolean => (
  process.env.NODE_ENV === 'development' && isDevelopmentEnvironment(host, includeLoopback)
);

/**
 * Provides a convenience function for determining if the application is explicitly
 * running in a local development environment such as localhost or a local IPV4 or
 * IPV6 address. If the host argument is falsy, particularly `undefined` or an
 * empty `string`, the host will default to `globalThis?.location?.hostname`.
 *
 * ```typescript
 *     isDevelopmentEnvironment('http://localhost:4000'); // true
 *     isDevelopmentEnvironment('http://127.0.0.1'); // true
 *     isDevelopmentEnvironment('somedomain.local'); // true
 *     isDevelopmentEnvironment('https://somedomain.com'); // false
 * ```
 */
export const isDevelopmentEnvironment = (host?: string, includeLoopback = true): boolean => {
  const domain  = host || globalThis?.location?.hostname;
  const isLocal = (
    contains(domain, 'localhost') ||
    contains(domain, '192.168') ||
    contains(domain, '.local')
  );
  return !!(includeLoopback ? (isLocal || isCommonLoopbackIP(host)) : isLocal);
};

/**
 * Provides a utility which determines if the application is being ran from a
 * common localhost loopback IP address (127.0.0.1).
 *
 * ```typescript
 *     isCommonLoopbackIP('http://localhost:4000'); // false
 *     isCommonLoopbackIP('http://127.0.0.1'); // true
 * ```
 *
 * @returns `true` if the application is being run from a localhost loopback
 *          address (127.0.0.1), otherwise, `false`.
 */
export const isCommonLoopbackIP = (host?: string): boolean => (
  !!contains(host || globalThis?.location?.hostname, '127.0.0.1')
);

/**
 * Provides a convenience function for determining if the application is running
 * in a local development environment.
 */
export const isLocal = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.local
);

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod dev environment.
 */
export const isDev = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.dev
);

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod qa environment.
 */
export const isQA = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.qa
);

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod staging environment.
 */
export const isStaging = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.stg
);

/**
 * Provides a convenience function for determining if the application is running
 * in a production environment (us, eu).
 */
export const isProduction = (host?: string): boolean => (
  isUSProduction(host) || isEUProduction(host)
);

/**
 * Provides a convenience function for determining if the application is running
 * in the US Production environment.
 */
export const isUSProduction = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.us
);

/**
 * Provides a convenience function for determining if the application is running
 * in the EU Production environment.
 */
export const isEUProduction = (host?: string): boolean => (
  getDeployedEnvironment(host) === Environment.eu
);

/**
 * Provides a convenience function for determining if the application is running
 * in a pre-prod environment (dev, dev1, dev2, dev3, qa, qa1, qa2, staging, stg1
 * stg2).
 */
export const isPreProd = (host?: string): boolean => (
  !isLocal(host) && !isProduction(host)
);

/**
 * Provides a convenience function which returns the environment in which the
 * application is executing.
 *
 * ```typescript
 *
 *   import {getEnvironment, Environment} from '@ion/core/src/environment/environment';
 *
 *   const env: Environment = getEnvironment();
 *
 * ```
 */
export const getEnvironment = (host?: string): Environment => (
  getDeployedEnvironment(host)
);

/**
 * @private
 *
 * Determines the environment to which the application has been deployed.
 */
export const getDeployedEnvironment = (src?: string): Environment => {
  const host =  src || globalThis.location.host;
  if (!isDevelopmentEnvironment(host)) {
    const match = Object.keys(Environment).find(env => contains(host, env));
    const env   = !match && (contains(host, 'staging') || contains(host, 'stg')) ? Environment.stg : match;
    if (!env) {
      return Environment[!contains(host, 'co.uk') ? 'us' : 'eu'];
    }
    return Environment[env];
  }
  return Environment.local;
};

/**
 * Provides a convenience function which returns the context of iOn deployment
 * i.e. iOn Suite or reseller (assistcargo, etc).
 *
 * ```typescript
 *
 *   import {getApplicationContext} from '@ion/core/src/environment/environment';
 *
 *   const context: ApplicationContext = getApplicationContext();
 *
 * ```
 */
export const getApplicationContext = (src?: string): ApplicationContext => {
  const host =  src || globalThis.location.host;
  const keys = Object.keys(ApplicationContext);
  for (const key of keys) {
    if (contains(host, key)) {
      return ApplicationContext[key];
    }
  }
  return ApplicationContext.ion;
};

/**
 * Provides an async alias to loadConfig.
 */
export const getConfig = async(): Promise<Config | undefined> => await loadConfig();

/**
 * The loadConfig method loads the external environment.json configuration
 * of the deployed application. This method is to be invoked prior to the
 * initial render call of the application.
 *
 * The environment.json document defines static keys referenced by the
 * CalAmp iOn UI for configuring external environment configurations, such
 * as the application key, service root url, etc.
 *
 * Keys are defined within environment.json at design time by engineering,
 * and the values for which are managed by Product Operations based on the
 * deployment context / environment.
 *
 * For a list of all available environment keys (and their default values),
 * see /src/static/environment.json. Likewise, additional keys (and their
 * default values) can be defined in environment.json.
 *
 * ```typescript
 *
 *   // load the environment.json configuration, then render!
 *     loadConfig(() => {
 *       render();
 *   });
 *
 * ```
 *
 * @method loadConfig
 * @param  {function} callback to be invoked upon loading of environment.json.
 * @param  {bool} noCache When true (default), indicates that the environment.json
 *         document is to be loaded via a simple cache busting strategy whereby a
 *         `ts` (timestamp) parameter is appended to the query string with the value
 *         of the current time.
 * @param  {object} source Allows for providing a source configuration to be used as
 *          an alternative to async retrieval.
 */
export const loadConfig = async(
  cb?: (config: Config | null) => void | undefined, noCache = true, source?: Config): Promise<Config | undefined> => {
  if (!_config) {
    if (!source) {
      info('Loading Environment Configuration ...');
      const params = qs.parse(location.search, {ignoreQueryPrefix: true});
      const url    = resolveQuery('/environment.json', noCache);
      try {
        const response: AxiosResponse = await axios.get(url, {headers: {'content-type': 'application/json'}});
        _config = response?.data;
        info('Environment Configuration Successfully Loaded.');
        //TODO - Temporary provide ability to switch between OIDC & non OIDC auth flows
        overrideConfigValue(_config, params);
        cb?.(_config);
        return _config;
      } catch (err) {
        error(`Environment Configuration error, could not retrieve ${url}.`);
        cb?.(null);
      }
    } else {
      info('Environment Configuration initialized from source.');
      _config = source;
      cb?.(_config as Config);
      return _config;
    }
  } else {
    cb?.(_config);
    return _config;
  }
};

/**
 * The `loadCORSConfig` function loads an external environment.json configuration
 * for the deployed application via CORS. This function fetches the configuration
 * from a specified base URL and should be invoked prior to initializing specific
 * APIs which require the configuration. One very important distinction is that
 * the `loadCORSConfig` is primarily used by iOn Monorepo Micro Frontend Remotes.
 * This is necessary as, should the Remotes be loaded within a host which does
 * not contain an environment.json, the Remote will fail to initialize.
 *
 * ```typescript
 *   // load the cross domain environment.json configuration ...
 *   try {
 *     const baseUrl = 'https://some.domain.com';
 *     const config  = await loadCORSConfig(baseUrl);
 *     ...
 *   } catch (err) {
 *     ...
 *   }
 * ```
 *
 * @param   baseUrl The base URL from which to retrieve the environment.json file.
 *          This is typically the root URL of the domain from which the environment
 *          json document is served.
 * @returns A promise that resolves with the parsed JSON content of the environment
 *          json document.
 * @throws  If there is an error fetching or parsing the configuration file.
 *          This could be due to network issues, CORS configuration problems, or
 *          invalid JSON in the environment.json file.
 */
export const loadCORSConfig = async(baseUrl: string, noCache = true, headers?: any): Promise<Config> => {
  const url = resolveQuery(`${baseUrl}/environment.json`, noCache);
  try {
    const response: AxiosResponse = await axios.get(url, {
      withCredentials : false,
      headers         : headers || {
        'Cache-Control' : 'no-cache'
      }
    });
    _config = response?.data;
    return _config;
  } catch (err) {
    error(`An Error occurred while attempted to load Environment Configuration from ${url}`, err);
    throw err;
  }
};

/**
 * Loads the environment.json configuration from a remote URL based on the provided
 * environment mapping and environment value. If the provided environment can be
 * resolved but fails to load, this function will fallback to loading the default
 * `environment.json` configuration.
 *
 * ```typescript
 *   const envMap: Record<Environment, string> = {
 *     [Environment.qa] : 'https://qa.example.com',
 *     [Environment.eu] : 'https://admin.calamp.co.uk',
 *     [Environment.us] : 'https://admin.calamp.com'
 *   };
 *   const config = await loadExternalConfig(envMap, Environment.qa);
 * ```
 *
 * @param   envMap A mapping of `Environment` enum values and their respective domains.
 * @param   env An `Environment` enum value which specified the environment from which
 *          their `environment.json` documents are to be loaded.
 * @returns A promise that resolves with the parsed JSON content of the `environment.json`
 *          document loaded from the given environment.
 * @throws  If the environment is not found in the map raised an Exception.
 */
export const loadExternalConfig = async(
  envMap: Environments | Partial<Environments>,
  env: Environment,
  noCache = true
): Promise<Config | void> => {
  const baseUrl = envMap[env];
  if (baseUrl) {
    try {
      return await loadCORSConfig(baseUrl, noCache, {
        'Content-Type' : 'application/json'
      });
    } catch (err) {
      error(`An Error occurred while attempted to load external Environment Configuration from ${baseUrl}`, JSON.stringify(err));
    }
  } else {
    throw new Error(`Environment ${env} not found in environment map`);
  }
};

/**
 * Returns a cloned representation of the underlying environment.json
 * configuration.
 *
 * @method clone
 */
export const clone = (): object => {
  return Object.assign({}, _config);
};

/**
 * @private
 *
 * Provides a pseudo-private hook for setting the environment configuration
 * object internally.
 */
export const __inject__ = (config: any, force = false) => {
  if (!_config || force) {
    _config = config;
  }
};

export {get as default};

/**
 * @private
 *
 * Provides a convenience function to override environment.json entries without rebuilding app or
 * updating puppet config. Presently you can override only boolean entries, although we could extend it
 * to update any value. This can be done only for pre-prod environments.
 * Lets assume that environment.json has these entries
 * "featureFlagsEnabled" : false,
 * "oidcEnabled" : false,
 * To override them we launch ion as
 * https://qa.ion.calamp.com/?oidcEnabled=true&featureFlagsEnabled=true
 *
 */
const overrideConfigValue = (config, params) => {
  for (const prop of Object.keys(params)) {
    const {[prop]: value} = config;
    if (!isNil(value)) {
      config[`_${prop}`] = config[prop];
      config[prop] = params[prop]?.toLowerCase() === 'true' || false;
      info(`${prop} is ${params[prop]}`);
    }
  }
};

/**
 * Provides a utility function which appends a cache busting query string parameter
 * to the provided URL when the `noCache` argument is set to `true` (default). This
 * function is used when loading the `environment.json` document.
 *
 * ```typescript
 *  const url = resolveQuery('https://calamp.com/environment.json', true);
 *  // https://calamp.com/environment.json?ts=1579036800000
 *  ...
 *
 *  const url = resolveQuery('https://calamp.com/environment.json', true, 'timestamp');
 *  // https://calamp.com/environment.json?timestamp=1579036800000
 *  ...
 *
 *  const url = resolveQuery(https://calamp.com/environment.json, false);
 *  // https://calamp.com/environment.json
 * ```
 *
 * @param   url The `url` for which the cache busting query string is to be resolved.
 * @param   noCache When `true`, appends a cache busting query string parameter to
 *          the provided URL. When `false`, the URL is returned as is.
 * @param   param The name of the query string parameter used for the cache busting.
 *          Defaults to `ts` (timestamp).
 * @returns The provided URL with a cache busting query string parameter appended
 *          when `noCache` is false. This allows for simple caching prevention when
 *          loading the `environment.json` document.
 */
const resolveQuery = (url: string, noCache = true, param = 'ts'): string => (
  noCache ? `${url}?${param}=${new Date().getTime()}` : url
);

/**
 * @private
 *
 * Provides a mechanism to run unit tests on non-exported functions.
 * NOT to be imported in production code.
 */
export const exportedForTesting = {
  overrideConfigValue
};
